import { Add, WarningAmberRounded, CheckCircleOutline } from "@mui/icons-material";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Grid
} from "@mui/material";
import { PremiumTextField } from "../PremiumInputs";
import { useState, useEffect } from "react";
import * as yup from "yup";
import { Formik } from "formik";
import { addClient, checkGlobalCredit, getPreferences } from "../../firebase/dataHandler";

function AddCustomerForm({ refresh, buttonText, style }) {
  const [open, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [globalWarning, setGlobalWarning] = useState(null);
  const [checkingCredit, setCheckingCredit] = useState(false);

  useEffect(() => {
    getPreferences().then(prefs => {
      setIsPremium(prefs?.isPremium === true);
    }).catch(console.error);
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
    setGlobalWarning(null);
  };

  const handleClose = () => {
    setOpen(false);
    setGlobalWarning(null);
  };

  const checkCredit = async (mobileNumber) => {
    if (!mobileNumber || mobileNumber.length !== 10) {
      setGlobalWarning(null);
      return;
    }
    setCheckingCredit(true);
    try {
      const data = await checkGlobalCredit(mobileNumber);
      if (data && data.active_loans > 0) {
        setGlobalWarning(data.active_loans);
      } else {
        setGlobalWarning(0); // Clean
      }
    } catch (error) {
      console.error(error);
    }
    setCheckingCredit(false);
  };

  // Dynamic Validation Schema
  const getValidationSchema = () => {
    let schema = {
      name: yup.string().required("Name is required").min(3),
      surName: yup.string().min(3),
      townName: yup.string().required("Town is required"),
      contact: yup.string().matches(/^[0-9]{10}$/, "Must be exactly 10 digits"),
      altContact: yup.string().matches(/^[0-9]{10}$/, "Must be exactly 10 digits"),
      hasConsent: yup.boolean()
    };

    if (!isPremium) {
      schema.contact = schema.contact.required("Mobile number is required for Free users.");
    }

    return yup.object().shape(schema).test(
      'consent-check',
      null,
      function(obj) {
        if (obj.contact && obj.contact.length > 0 && !obj.hasConsent) {
          return this.createError({
            path: 'hasConsent',
            message: 'You must obtain and confirm customer consent to use their mobile number.'
          });
        }
        return true;
      }
    );
  };

  const formValues = {
    name: "",
    surName: "",
    townName: "",
    contact: "",
    altContact: "",
    idProof: "",
    hasConsent: false
  };

  return (
    <div>
      <IconButton size="small" style={style} color="primary" onClick={handleClickOpen}>
        <Add /> {buttonText}
      </IconButton>
      
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
          }
        }}
      >
        {(isLoading || checkingCredit) && <LinearProgress color={checkingCredit ? "warning" : "primary"} sx={{ height: 3, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #15C358, #109441)' } }} />}
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid var(--border-subtle)', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: 'var(--text-primary)', fontFamily: 'Inter' }}>
            Add New Customer
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'var(--text-muted)', fontFamily: 'Inter' }}>
            Enter customer details to create a profile
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          
          <Formik
            initialValues={formValues}
            validationSchema={getValidationSchema()}
            validateOnChange={true}
            validateOnBlur={true}
            onSubmit={(values, actions) => {
              setLoading(true);
              addClient(values).then((result) => {
                actions.setSubmitting(false);
                setLoading(false);
                actions.resetForm();
                refresh();
                handleClose();
              }).catch(err => {
                actions.setSubmitting(false);
                setLoading(false);
                alert(err);
              });
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                
                {/* Global Credit Alert */}
                {globalWarning !== null && values.contact?.length === 10 && (
                  <Alert 
                    severity={globalWarning > 0 ? "warning" : "success"}
                    icon={globalWarning > 0 ? <WarningAmberRounded /> : <CheckCircleOutline />}
                    sx={{ borderRadius: 2, mb: 1 }}
                  >
                    {globalWarning > 0 ? 
                      <Typography variant="body2" fontWeight="bold">Credit Warning: This customer has {globalWarning} active loan(s) with other financiers.</Typography> : 
                      <Typography variant="body2" fontWeight="bold">Clear: No external active loans found in the registry.</Typography>
                    }
                  </Alert>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      id="name"
                      label="First Name"
                      type="text"
                      required
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      id="surName"
                      label="Last Name"
                      type="text"
                      value={values.surName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <PremiumTextField
                      id="townName"
                      label="Town/City Name"
                      type="text"
                      required
                      value={values.townName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.townName && Boolean(errors.townName)}
                      helperText={touched.townName && errors.townName}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      id="contact"
                      label={isPremium ? "Mobile No." : "Mobile No. (Required)"}
                      type="tel"
                      required={!isPremium}
                      value={values.contact}
                      onChange={(e) => {
                        handleChange(e);
                        if (e.target.value.length === 10) {
                          checkCredit(e.target.value);
                        } else {
                          setGlobalWarning(null);
                        }
                      }}
                      onBlur={handleBlur}
                      inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                      error={touched.contact && Boolean(errors.contact)}
                      helperText={touched.contact && errors.contact}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      id="altContact"
                      label="Alt Mobile No."
                      type="tel"
                      value={values.altContact}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <PremiumTextField
                      id="idProof"
                      name="idProof"
                      label="Aadhar No. / PAN No."
                      type="text"
                      value={values.idProof}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>
                </Grid>

                {/* Consent Checkbox - Only show if contact is being entered */}
                {values.contact && values.contact.length > 0 && (
                  <Box sx={{ background: 'var(--bg-elevated)', p: 1.5, borderRadius: 2, border: '1px solid var(--border-subtle)', mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          id="hasConsent"
                          name="hasConsent"
                          checked={values.hasConsent} 
                          onChange={handleChange}
                          sx={{ color: 'var(--accent-green)', '&.Mui-checked': { color: 'var(--accent-green)' } }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontFamily: 'Inter' }}>I confirm I have obtained consent from the customer as per the <a href="/terms" target="_blank" rel="noreferrer" style={{color: 'var(--accent-green)'}}>Terms & Conditions</a> to verify their credit profile.</Typography>}
                    />
                    {touched.hasConsent && errors.hasConsent && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', ml: 4, fontFamily: 'Inter' }}>
                        {errors.hasConsent}
                      </Typography>
                    )}
                  </Box>
                )}

                <DialogActions sx={{ pt: 3, px: 0, pb: 0, borderTop: '1px solid var(--border-subtle)', mt: 3 }}>
                    <Button 
                      onClick={handleClose} 
                      sx={{ color: 'var(--text-muted)', fontFamily: 'Inter', fontWeight: 600, px: 3 }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      variant="contained" 
                      onClick={handleSubmit}
                      disabled={isLoading || checkingCredit}
                      sx={{ 
                        fontFamily: 'Inter', fontWeight: 700, px: 4, py: 1, borderRadius: 2,
                        background: 'linear-gradient(135deg, #15C358, #109441)',
                        color: '#fff',
                        boxShadow: '0 4px 14px rgba(21, 195, 88, 0.4)',
                        '&:hover': { background: '#109441' },
                        '&.Mui-disabled': { background: 'var(--border-subtle)', color: 'var(--text-muted)' }
                      }}
                    >
                      Create Customer
                    </Button>
                </DialogActions>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AddCustomerForm;
